import React, { useContext } from "react";

import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  Grid,
  InputAdornment,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import { makeStyles, withStyles } from "tss-react/mui";

import { colors } from "@/theme/overrides/colors";
import { handleChange } from "@/utils/hookForm";
import MeatIcon from "@assets/icons/beef.png";
import FruitIcon from "@assets/icons/fruits.png";
import DairyIcon from "@assets/icons/milk.png";
import StarchIcon from "@assets/icons/starch.png";
import SugarIcon from "@assets/icons/sugar-cube.png";
import FatIcon from "@assets/icons/trans-fats-free.png";
import VegetableIcon from "@assets/icons/vegetable.png";

import CalculationTable from "./CalculationTable";
import MealPlanFormStore from "../store/MealPlanFormStore";

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: theme.spacing(4),
    backgroundColor: "#fafafa",
  },
  icon: {
    marginRight: theme.spacing(3),
    height: 24,
    width: 24,
  },
  bold: {
    display: "inline",
    fontWeight: "bold",
  },
  autosuggest: {
    color: colors.gray,
    fontSize: 14,
    fontWeight: "bold",
  },
}));

const CustomTooltip = withStyles(Tooltip, (theme: Theme) => ({
  tooltip: {
    backgroundColor: colors.dark,
    color: theme.palette.common.white,
    padding: theme.spacing(2),
    fontSize: 14,
  },
}));

const FoodExchangeCalculator: React.FC = observer(() => {
  const { classes } = useStyles();

  const store = useContext(MealPlanFormStore.Context);
  const fel = store.foodExchange;

  const _suggestionInfo = (
    suggestedValue: number,
    currentValue: number | string | undefined
  ) => {
    const title =
      currentValue != null || currentValue == "0"
        ? `Suggested value: ${suggestedValue}`
        : `Suggested remaining value: ${suggestedValue}`;
    return (
      <CustomTooltip title={title}>
        <InfoIcon fontSize="small" />
      </CustomTooltip>
    );
  };

  return (
    <Grid className={classes.root} container>
      <Grid item container xs={7}>
        <Grid item container xs={12} spacing={2}>
          <Grid item xs={3}>
            <Box display="flex" marginTop={3}>
              <img className={classes.icon} src={VegetableIcon} alt="" />
              <Typography className={classes.bold}>Vegetables</Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <TextField
              value={fel.values.vegetables}
              onChange={handleChange((value) => fel.set("vegetables", value), {
                maxDecimalPlace: 2,
              })}
              type="number"
              size="small"
              margin="dense"
            />
          </Grid>
        </Grid>
        <Grid item container xs={12} spacing={2}>
          <Grid item xs={3}>
            <Box display="flex" marginTop={3}>
              <img className={classes.icon} src={FruitIcon} alt="" />
              <Typography className={classes.bold}>Fruits</Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <TextField
              value={fel.values.fruits}
              onChange={handleChange((value) => fel.set("fruits", value), {
                maxDecimalPlace: 2,
              })}
              type="number"
              size="small"
              margin="dense"
            />
          </Grid>
        </Grid>
        <Grid item container xs={12} spacing={2}>
          <Grid item xs={3}>
            <Box display="flex" marginTop={3}>
              <img className={classes.icon} src={SugarIcon} alt="" />
              <Typography className={classes.bold}>Treats</Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <TextField
              value={fel.values.treats}
              onChange={handleChange((value) => fel.set("treats", value), {
                maxDecimalPlace: 2,
              })}
              type="number"
              size="small"
              margin="dense"
            />
          </Grid>
        </Grid>
        <Grid item container xs={12} spacing={2}>
          <Grid item xs={3}>
            <Box display="flex" marginTop={3}>
              <img className={classes.icon} src={DairyIcon} alt="" />
              <Typography className={classes.bold}>Dairy</Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <TextField
              value={fel.values.dairy}
              onChange={handleChange((value) => fel.set("dairy", value), {
                maxDecimalPlace: 2,
              })}
              type="number"
              size="small"
              margin="dense"
              helperText="Dairy"
            />
          </Grid>
        </Grid>
        <Grid item container xs={12} spacing={2}>
          <Grid item xs={3}>
            <Box display="flex" marginTop={3}>
              <img className={classes.icon} src={StarchIcon} alt="" />
              <Typography className={classes.bold}>Starches</Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <TextField
              value={fel.values.starches}
              onChange={handleChange((value) => fel.set("starches", value), {
                maxDecimalPlace: 2,
              })}
              type="number"
              size="small"
              margin="dense"
              helperText="Starches"
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    className={classes.autosuggest}
                    position="end"
                  >
                    {_suggestionInfo(
                      fel.autosuggest.starches,
                      fel.values.starches
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
        <Grid item container xs={12} spacing={2}>
          <Grid item xs={3}>
            <Box display="flex" marginTop={3}>
              <img className={classes.icon} src={MeatIcon} alt="" />
              <Typography className={classes.bold}>Meat and Seafood</Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <TextField
              value={fel.values.meatAndSeafood}
              onChange={handleChange(
                (value) => fel.set("meatAndSeafood", value),
                {
                  maxDecimalPlace: 2,
                }
              )}
              type="number"
              size="small"
              margin="dense"
              helperText="Meat and Seafood"
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    className={classes.autosuggest}
                    position="end"
                  >
                    {_suggestionInfo(
                      fel.autosuggest.meatAndSeafood,
                      fel.values.meatAndSeafood
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
        <Grid item container xs={12} spacing={2}>
          <Grid item xs={3}>
            <Box display="flex" marginTop={3}>
              <img className={classes.icon} src={FatIcon} alt="" />
              <Typography className={classes.bold}>Fats and oil</Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <TextField
              value={fel.values.fatsAndOil}
              onChange={handleChange((value) => fel.set("fatsAndOil", value), {
                maxDecimalPlace: 2,
              })}
              type="number"
              size="small"
              margin="dense"
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    className={classes.autosuggest}
                    position="end"
                  >
                    {_suggestionInfo(
                      fel.autosuggest.fatsAndOil,
                      fel.values.fatsAndOil
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
        <Grid item container xs={12} spacing={2}>
          <Grid item xs={3}>
            <Box display="flex" marginTop={3}>
              <img className={classes.icon} src={MeatIcon} alt="" />
              <Typography className={classes.bold}>Plant-based Meat</Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <TextField
              value={fel.values.plantBasedMeat}
              onChange={handleChange(
                (value) => fel.set("plantBasedMeat", value),
                {
                  maxDecimalPlace: 2,
                }
              )}
              type="number"
              size="small"
              margin="dense"
              helperText="Plant-base Meat"
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={5}>
        <CalculationTable />
      </Grid>
    </Grid>
  );
});

export default FoodExchangeCalculator;
